import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { AutoUnsubscribe, Subscription, StorageUtil, Observable, Store, AppFields, take, AppTypes, combineLatest, ValidEmail } from '../../../../store';
import { ClubMemberService, AlertService } from '../../../../services';
import { HttpErrorResponse } from '@angular/common/http';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import * as moment from 'moment';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-player-type',
  templateUrl: './player-type.component.html',
  styleUrls: ['./player-type.component.css']
})
@AutoUnsubscribe
export class PlayerTypeComponent implements OnInit {

  @Input() teamID: number;
  @Input() teams = [];
  @Output() close = new EventEmitter();
  @Output() save = new EventEmitter();
  @Output() next = new EventEmitter();
  @Output() create = new EventEmitter();

  form: FormGroup;
  sub: Subscription = new Subscription();
  error: string;
  years = [];
  positions: Observable<any[]>;
  submitted = false;

  constructor(
    private fb: FormBuilder,
    private service: ClubMemberService,
    private as: AlertService,
    private spinner: Ng4LoadingSpinnerService,
    private store: Store<any>,
    public route: ActivatedRoute
  ) {
    this.buildForm();

    this.positions = this.store.select(AppFields.App, AppFields.PlayerPositions);

    for (let i = new Date().getFullYear(); i <= 2040; i++) {
      this.years.push(i);
    }
  }

  ngOnInit() {
    this.form.patchValue({
      teamID: this.teamID
    });
    this.positions.pipe(take(1)).subscribe(list => list === null && this.store.dispatch({
      type: AppTypes.GetPlayerPositions,
      payload: {
        clubID: StorageUtil.clubID
      }
    }));
  }

  private buildForm() {
    this.form = this.fb.group({
      by: [null, Validators.required],
      type: [null, Validators.required],
      addType: [null],
      userID: [null],
      email: [null],
      firstName: [null],
      lastName: [null],
      middleName: [null],
      dob: [null],
      gender: [null],
      position: [null],
      gradYear: [null],
      teamType: [null],
      teamID: [null, Validators.required]
    });

    if (+this.route.snapshot.params.typeID > 0) {
      this.form.patchValue({
        teamType: +this.route.snapshot.params.typeID
      });
    }

    this.sub.add(this.form.controls.by.valueChanges.subscribe((by) => by !== this.by && this.updateValueAndValidity(by, this.addType)));
    this.sub.add(this.form.controls.addType.valueChanges.subscribe((addType) => addType !== this.addType && this.updateValueAndValidity(this.by, addType)));
    this.sub.add(
      this.form.controls.dob.valueChanges.subscribe((dob) => dob !== this.form.value.dob && this.updateValueAndValidity(this.by, this.addType))
    );
  }

  onClose() {
    this.close.emit();
  }

  onNext() {
    this.next.emit(this.form.value);
  }

  onComplete() {
    this.submitted = true;
    console.log(this.form);
    if (this.form.invalid) {
      this.as.warn('Heads Up! You must fill in all required items.');
      return;
    }

    this.spinner.show();
    let data;
    if (this.addType === 'standard') {
      data = {
        firstName: this.form.value.firstName,
        lastName: this.form.value.lastName,
        middleName: this.form.value.middleName || '',
        dob: this.form.value.dob,
        gender: this.form.value.gender,
        positionID: this.form.value.position,
        email: '',
        TeamTypeID: this.form.value.teamType
      };
      if (this.age > 12) {
        data.email = this.email;
        data.gradYear = this.form.value.gradYear;
      }
    }

    (this.by === 'id' ? this.service.validateNewClubUserID(StorageUtil.clubID, this.form.value.teamID, this.userID)
      : this.addType === 'quick' ? this.service.validateNewClubUserEmail(StorageUtil.clubID, this.form.value.teamID, this.email)
        : this.service.validateNewClubUserData(StorageUtil.clubID, this.form.value.teamID, data)
    ).subscribe(
      (players) => {
        this.spinner.hide();
        if (players.length) {
          this.next.emit(players.map(player => ({
            ...player,
            playerType: this.type,
            teamID: this.form.value.teamID,
            by: this.by
          })));
          this.save.emit({
            ...data,
            playerType: this.type,
            teamID: this.form.value.teamID
          });
        } else {
          this.create.emit({
            ...data,
            playerType: this.type,
            teamID: this.form.value.teamID,
            by: this.by
          });
        }
      },
      (err: HttpErrorResponse) => {
        if (typeof err.error === 'string') {
          this.error = err.error;
        } else {
          this.as.error('Something went wrong.');
        }
        this.spinner.hide();
      }
    );
  }

  getTeams(): any[] {
    return (this.teams || []).filter(t => t.teamTypeID === +this.form.value.teamType);
  }

  hasError(key: string) {
    const c: AbstractControl = this.form.get(key);
    return this.submitted && c.errors;
  }

  private updateValueAndValidity(by, addType) {
    if (by === 'id') {
      this.form.controls.userID.setValidators([Validators.required, Validators.min(1)]);
      this.form.controls.addType.setValidators(null);
    } else {
      this.form.controls.userID.setValidators(null);
      this.form.controls.addType.setValidators([Validators.required]);
    }
    if (addType === 'quick' || by === 'id') {
      this.form.controls.email.setValidators(by === 'id' ? null : [Validators.required, Validators.email, Validators.pattern(ValidEmail)]);
      this.form.controls.gradYear.setValidators(null);
      this.form.controls.firstName.setValidators(null);
      this.form.controls.lastName.setValidators(null);
      this.form.controls.dob.setValidators(null);
      this.form.controls.gender.setValidators(null);
      this.form.controls.position.setValidators(null);
    } else {
      this.form.controls.email.setValidators(this.age < 13 || this.form.value.teamType != 1 ? null : [Validators.required, Validators.email, Validators.pattern(ValidEmail)]);
      this.form.controls.gradYear.setValidators(this.age < 13 ? null : [Validators.required]);
      this.form.controls.firstName.setValidators([Validators.required]);
      this.form.controls.lastName.setValidators([Validators.required]);
      this.form.controls.dob.setValidators([Validators.required]);
      this.form.controls.gender.setValidators([Validators.required]);
      this.form.controls.position.setValidators([Validators.required]);
    }

    const controls = ['email', 'gradYear', 'firstName', 'lastName', 'dob', 'gender', 'position'];
    for (const c of controls) {
      this.form.get(c).updateValueAndValidity();
    }

    this.form.updateValueAndValidity();
  }

  get by(): string {
    return this.form.value.by;
  }

  get userID(): number {
    return +this.form.value.userID;
  }

  get email(): string {
    return this.form.value.email;
  }

  get type(): string {
    return this.form.value.type;
  }

  get addType(): string {
    return this.form.value.addType;
  }

  get age(): number {
    return this.form.value.dob ? moment().diff(moment(new Date(this.form.value.dob)), 'years') : 0;
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/components/add-player/player-type/player-type.component.ts